import * as React from 'react'
import {ReactElement} from 'react'
import {Location} from 'react-router-dom'
import {getQueryParameter} from '../../../common/util/Natutil'
import {NatGetToken} from '../../token/repository/NatGetToken'
import {Nullable} from '../../../../app/TypeHelpers'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet
} from '../../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {Messages} from '../../../../Messages'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {PageTitle} from '../common/resident/NatResidentApplicationsCommon'
import {Box} from '@mui/material';
import {
    HeadingAreaMessageComponentForResident
} from '../../../../components/shared/pageBaseComponents/HeadingAreaMessageComponentForResident'
import NatNoAuthenticationKeyForm from './resident/NatNoAuthenticationKeyForm'
import NatRidgeRepo from '../../ridge/repository/NatRidgeRepo'
import NatTokenRepo from '../../token/repository/NatTokenRepo'
import {NatGetRidge, RidgeData} from '../../ridge/repository/NatGetRidge'
import {NatResidentUserRegistrationInfo} from './NatResidentUserRegistrationApplicationInterface'
import {NatApplicationRepo} from '../repository/NatApplicationRepo'
import {
    NatPostResidentUserRegistrationRequest,
    NatPostResidentUserRegistrationResponse
} from '../repository/NatPostResidentUserRegistration'
import {NatResidentStepper} from '../../common/resident/NatResidentStepper'
import {BreakPcOnly} from '../../../../styledComponents/DisplayItem'

interface Props {
    tokenRepo: NatTokenRepo,
    ridgeRepo: NatRidgeRepo,
    applicationRepo: NatApplicationRepo
    location: Location
}

interface State {
    condominiumCd: string
    condominiumName: string
    ridgeList: RidgeData[]
    selectedRidgeCd: Nullable<string>
    selectedRidgeName: Nullable<string>
    roomNumber: Nullable<string>
    familyName: Nullable<string>
    firstName: Nullable<string>
    familyNameKana: Nullable<string>
    firstNameKana: Nullable<string>
    residenceStyle: Nullable<string>
    mailAddress: string
    bankAccountNumberLast4Digits: Nullable<string>
    isLoadCompleted: boolean
}

class NatResidentUserRegistrationInputPage
    extends React.Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {
    queryToken: string

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            condominiumCd: '',
            condominiumName: '',
            ridgeList: [],
            selectedRidgeCd: null,
            selectedRidgeName: null,
            roomNumber: null,
            familyName: null,
            firstName: null,
            familyNameKana: null,
            firstNameKana: null,
            residenceStyle: null,
            mailAddress: '',
            bankAccountNumberLast4Digits: null,
            isLoadCompleted: false
        }
        this.queryToken = getQueryParameter('token', this.props.location.search)
        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {eventEmitter, location} = this.props
        const locationState = this.props.location?.state as any;

        await eventEmitter.showLoading()
        try {

            if (locationState) {
                this.setState({
                    selectedRidgeName: locationState.selectedRidgeName ?? '',
                    roomNumber: locationState.roomNumber ?? '',
                    familyName: locationState.familyName ?? '',
                    firstName: locationState.firstName ?? '',
                    familyNameKana: locationState.familyNameKana ?? '',
                    firstNameKana: locationState.firstNameKana ?? '',
                    residenceStyle: locationState.residenceStyle ?? '',
                });
            }
            const tokenResponse = await this.getTokenInfo()
            if (tokenResponse === null || tokenResponse.details === null) {
                this.props.navigate('/resident/error', {state: {message: Messages.ERESR0003.toString()}})
                return
            }
            const ridgeResponse = await this.getRidgeList(tokenResponse.details.condominiumCd)

            this.setState({
                condominiumCd: tokenResponse.details.condominiumCd,
                condominiumName: tokenResponse.details.condominiumName,
                ridgeList: ridgeResponse?.details ?? [],
                selectedRidgeCd: location.state?.selectedRidgeCd ?? null,
                selectedRidgeName: location.state?.selectedRidgeName ?? null,
                roomNumber: location.state?.roomNumber ?? null,
                familyName: location.state?.familyName ?? null,
                firstName: location.state?.firstName ?? null,
                familyNameKana: location.state?.familyNameKana ?? null,
                firstNameKana: location.state?.firstNameKana ?? null,
                residenceStyle: location.state?.residenceStyle ?? null,
                mailAddress: tokenResponse.details?.mailAddress,
                bankAccountNumberLast4Digits: location.state?.bankAccountNumberLast4Digits ?? null,
            })
        } finally {
            eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    async getTokenInfo(): Promise<Nullable<NatGetToken>> {
        return await this.props.asyncCallFunction(
            this.props.tokenRepo.getToken.bind(this.props.tokenRepo, this.queryToken),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
            })
    }

    async getRidgeList(condominiumCd: string): Promise<Nullable<NatGetRidge>> {
        return await this.props.asyncCallFunction(
            this.props.ridgeRepo.getRidgeList.bind(this.props.ridgeRepo, condominiumCd),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
            })
    }

    async onClickApplyConfirmButton(applyContent: NatResidentUserRegistrationInfo): Promise<void> {
        const {eventEmitter} = this.props

        await eventEmitter.showLoading()
        try {
            const roomNumber = applyContent.roomNumber.length === 3 ?
                `0${applyContent.roomNumber}` : applyContent.roomNumber

            const request: NatPostResidentUserRegistrationRequest = {
                ...applyContent,
                ridgeCd: applyContent.selectedRidgeCd,
                roomNumber: roomNumber,
                token: this.queryToken,
                confirmed: false
            }

            if (await this.applyResidentUserRegistration(request)) {
                const locationState: NatResidentUserRegistrationInfo = {...applyContent}
                this.props.navigate(
                    `/resident/noAuthenticationKey/residentUserConfirm?token=${this.queryToken}`,
                    {state: locationState}
                )
            }
        } finally {
            eventEmitter.closeLoading()
        }
    }

    async applyResidentUserRegistration(request: NatPostResidentUserRegistrationRequest): Promise<boolean> {
        const {applicationRepo} = this.props
        const {condominiumCd} = this.state

        const response: Nullable<NatPostResidentUserRegistrationResponse> = await this.props.asyncCallFunction(
            applicationRepo.applyResidentUserRegistration.bind(
                applicationRepo, condominiumCd, request),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
            },
            {
                defaultErrorTitle: Messages.EOPCR0016.toString(),
                defaultErrorTitlePriority: true,
                replacementDetailErrorMessages: [{
                    targetMessageId: Messages.EOPCA0004,
                    replacementMessageId: Messages.EOPCR0015
                }]
            },
        )
        return !!(response && response.requestError === null)
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                <BlockSpacer height={'30px'}/>
                <NatResidentStepper
                    stepList={[
                        {label: '手続き説明'},
                        {label: <>マンション・<BreakPcOnly/>メールアドレス登録</>},
                        {label: 'メール受信'},
                        {label: '申請者情報登録',isActive: true},
                        {label: '申請者情報確認'},
                        {label: '申請提出完了', isSquare: true}
                    ]}
                />
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    申請者情報登録
                </PageTitle>
                <Box height={'20px'}/>
                <HeadingAreaMessageComponentForResident eventEmitter={this.props.eventEmitter}/>
                <NatNoAuthenticationKeyForm
                    {...this.props}
                    {...this.state}
                    submitButtonLabel={'申請内容を確認する'}
                    onClickApplyConfirmButton={this.onClickApplyConfirmButton}
                />
            </>
        )
    }
}

export default createResidentWithErrorHandlingPage(NatResidentUserRegistrationInputPage)
